import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";

export type LinkStatus = "pending" | "success" | "error";

interface LinkProgressSingleDialogProps {
  open: boolean;
  status?: LinkStatus;
  onComplete: () => void;
}

const SUCCESS_AUTO_CLOSE_MS = 750;
const ERROR_AUTO_CLOSE_MS = 1800;

const LinkProgressSingleDialog = ({
  open,
  status = "pending",
  onComplete,
}: LinkProgressSingleDialogProps) => {
  const { t } = useTranslation();
  const [finished, setFinished] = useState(false);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const STEPS = [
    t("coachingNavigator.coachingCalendar.progressStep1"),
    t("coachingNavigator.coachingCalendar.progressStep2"),
    t("coachingNavigator.coachingCalendar.progressStep3"),
  ];

  const finalStepErrorText = t(
    "coachingNavigator.coachingCalendar.progressError",
    {
      defaultValue: "Linking failed. Please try again.",
    },
  );
  const finalStepPendingText = t(
    "coachingNavigator.coachingCalendar.progressFinalizing",
    {
      defaultValue: "Finalizing linkage…",
    },
  );

  useEffect(() => {
    if (!open) return;
    setFinished(false);
    const timer = window.setTimeout(() => setFinished(true), 600);
    return () => window.clearTimeout(timer);
  }, [open]);

  // Auto-close only after the fake loading finishes AND the real linkage has settled.
  useEffect(() => {
    if (!finished || status === "pending") return;
    const delay =
      status === "error" ? ERROR_AUTO_CLOSE_MS : SUCCESS_AUTO_CLOSE_MS;
    const timer = window.setTimeout(() => {
      onCompleteRef.current();
    }, delay);
    return () => window.clearTimeout(timer);
  }, [finished, status]);

  if (!open) {
    return null;
  }

  const resolved = finished && status !== "pending";
  const finalText =
    resolved && status === "error" ? finalStepErrorText : finalStepPendingText;
  const allSteps = [...STEPS, finalText];

  const renderStepIcon = (index: number) => {
    const isFinal = index === allSteps.length - 1;
    if (isFinal) {
      if (resolved && status === "error") {
        return <ErrorOutlineIcon sx={{ color: "error.main", fontSize: 32 }} />;
      }
      if (resolved && status === "success") {
        return <CheckCircleIcon sx={{ color: "#2E7D32", fontSize: 32 }} />;
      }
      return <CircularProgress size={28} sx={{ color: "#242C63" }} />;
    }
    if (finished) {
      return <CheckCircleIcon sx={{ color: "#2E7D32", fontSize: 32 }} />;
    }
    return <CircularProgress size={28} sx={{ color: "#242C63" }} />;
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: "8px", bgcolor: "#fff" } } }}
    >
      <DialogTitle>
        {t("coachingNavigator.coachingCalendar.creatingLinkage")}
      </DialogTitle>
      <DialogContent dividers>
        {allSteps.map((step, index) => {
          const isFinal = index === allSteps.length - 1;
          const isErrorFinal = isFinal && resolved && status === "error";
          return (
            <Box key={index}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  py: 2,
                  px: 1,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {renderStepIcon(index)}
                </Box>
                <Typography
                  variant="body1"
                  sx={[
                    {
                      color: isErrorFinal ? "error.main" : "text.primary",
                    },
                    finished
                      ? {
                          fontWeight: 400,
                        }
                      : {
                          fontWeight: 500,
                        },
                  ]}
                >
                  {step}
                </Typography>
              </Box>
              {index < allSteps.length - 1 && (
                <Divider sx={{ borderColor: "#DFE1E3" }} />
              )}
            </Box>
          );
        })}
      </DialogContent>
    </Dialog>
  );
};

export default LinkProgressSingleDialog;
